// src/features/WasherAnalysis/hooks/useWasherData.js
import { useState, useEffect, useMemo } from 'react';
import { getData } from '../../../services/storageService';

// ОНОВЛЕНА конфігурація програм пральних машин - синхронізована з TabsWrapper.js
export const WASHER_PROGRAMS = {
  // Програми з циклами (з CyclesTableUnified)
  mant: { label: 'Mantelería', unit: 'ciclos', type: 'cycles', color: 'primary' },
  sab: { label: 'Sábanas', unit: 'ciclos', type: 'cycles', color: 'primary' },
  pis: { label: 'Piscina', unit: 'ciclos', type: 'cycles', color: 'primary' },
  otr: { label: 'Otros', unit: 'ciclos', type: 'cycles', color: 'primary' },
  'otr-29': { label: 'Otros - programa 29', unit: 'ciclos', type: 'cycles', color: 'primary' },
  'otr-30': { label: 'Otros - programa 30', unit: 'ciclos', type: 'cycles', color: 'primary' },
  cent: { label: 'Centrífugo', unit: 'ciclos', type: 'cycles', color: 'primary' },
  
  // Програми з вагою (кг) - додаємо з TabsWrapper
  peso1: { label: 'Mantelería', unit: 'kg', type: 'weight', color: 'success' },
  peso2: { label: 'Sábanas', unit: 'kg', type: 'weight', color: 'success' },
  peso3: { label: 'Toallas', unit: 'kg', type: 'weight', color: 'success' },
  peso4: { label: 'Nórdicos', unit: 'kg', type: 'weight', color: 'success' },
  peso5: { label: 'Albornoces', unit: 'kg', type: 'weight', color: 'success' },
  peso6: { label: 'Toallas Color', unit: 'kg', type: 'weight', color: 'success' },
  peso7: { label: 'Premium', unit: 'kg', type: 'weight', color: 'success' },
  
  // Відходи (з rechazosLabels)
  toallas: { label: 'Rechazo Toallas', unit: 'kg', type: 'rejection', color: 'error' },
  nordicos: { label: 'Rechazo Nórdicos', unit: 'kg', type: 'rejection', color: 'error' },
  sabanas: { label: 'Rechazo Sábanas', unit: 'kg', type: 'rejection', color: 'error' },
  otros: { label: 'Rechazo Varios', unit: 'kg', type: 'rejection', color: 'error' }
};

export const useWasherData = (selectedProgram = null) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const allRecords = await getData('lainsa_records');
        console.log('Loaded washer program records:', allRecords);
        setRecords(Array.isArray(allRecords) ? allRecords : []);
      } catch (err) {
        console.error('Error loading washer program data:', err);
        setError('Failed to load washer program data');
        setRecords([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Обробляємо дані для всіх програм
  const allProgramsData = useMemo(() => {
    const result = {};
    
    Object.keys(WASHER_PROGRAMS).forEach(programKey => {
      const programRecords = records
        .filter(record => {
          // Для циклів пральних машин (lav_ciclos)
          if (WASHER_PROGRAMS[programKey].type === 'cycles') {
            const hasData = record.recordType === 'shift_report' && 
                           record.lav_ciclos && 
                           record.lav_ciclos[programKey] !== undefined &&
                           record.lav_ciclos[programKey] !== null &&
                           record.lav_ciclos[programKey] !== '';
            
            if (hasData) {
              console.log(`Found cycles data for ${programKey}:`, record.lav_ciclos[programKey]);
            }
            
            return hasData;
          }
          
          // Для відходів пральних машин (lav_rechazos)
          if (WASHER_PROGRAMS[programKey].type === 'rejection') {
            const hasData = record.recordType === 'shift_report' && 
                           record.lav_rechazos && 
                           record.lav_rechazos[programKey] !== undefined &&
                           record.lav_rechazos[programKey] !== null &&
                           record.lav_rechazos[programKey] !== '';
            
            if (hasData) {
              console.log(`Found rejection data for ${programKey}:`, record.lav_rechazos[programKey]);
            }
            
            return hasData;
          }
          
          // Для ваги (lavadoras)
          if (WASHER_PROGRAMS[programKey].type === 'weight') {
            const hasData = record.recordType === 'shift_report' && 
                           record.lavadoras && 
                           record.lavadoras[programKey] !== undefined &&
                           record.lavadoras[programKey] !== null &&
                           record.lavadoras[programKey] !== '';
            
            if (hasData) {
              console.log(`Found weight data for ${programKey}:`, record.lavadoras[programKey]);
            }
            
            return hasData;
          }
          
          return false;
        })
        .map(record => {
          let value = 0;
          
          if (WASHER_PROGRAMS[programKey].type === 'cycles') {
            // Для циклів - рахуємо кількість символів "|"
            const cyclesString = record.lav_ciclos[programKey] || '';
            value = cyclesString ? cyclesString.split('|').length - 1 : 0;
          } else if (WASHER_PROGRAMS[programKey].type === 'rejection') {
            // Для відходів - рахуємо кількість символів "|"
            const rejectionString = record.lav_rechazos[programKey] || '';
            value = rejectionString ? rejectionString.split('|').length - 1 : 0;
          } else {
            // Для ваги - просто числове значення
            value = Number(record.lavadoras[programKey]) || 0;
          }
          
          return {
            date: record.fecha,
            value: value,
            shift: record.turno,
            operators: record.operators
          };
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      console.log(`Program ${programKey} records:`, programRecords);

      // Розраховуємо статистику
      const values = programRecords.map(r => r.value).filter(v => v > 0);
      const statistics = values.length > 0 ? {
        total: values.reduce((a, b) => a + b, 0),
        average: values.reduce((a, b) => a + b, 0) / values.length,
        max: Math.max(...values),
        min: Math.min(...values),
        daysWithData: values.length,
        recordsCount: programRecords.length
      } : {
        total: 0, average: 0, max: 0, min: 0, daysWithData: 0, recordsCount: 0
      };

      result[programKey] = {
        data: programRecords,
        statistics,
        config: WASHER_PROGRAMS[programKey]
      };
    });

    return result;
  }, [records]);

  // Дані для обраної програми
  const selectedProgramData = selectedProgram ? allProgramsData[selectedProgram] : null;

  console.log('Selected program data:', selectedProgram, selectedProgramData);

  return {
    allProgramsData,
    selectedProgramData,
    loading,
    error,
    programsConfig: WASHER_PROGRAMS
  };
};


